/* eslint-disable react/require-default-props */
import { Box, Flex, FormControl, Select, Text, Tooltip } from '@chakra-ui/core';
import JoditEditor, { Jodit } from 'jodit-react';
import isObject from 'lodash/isObject';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdOutlineInfo } from 'react-icons/md';
import { OfferConditionsType } from '../../firebase/firestore/documents/candidateAudit';
import { LanguageMap } from '../../firebase/firestore/documents/user';
import i18n from '../../locales/i18n';
import colors from '../../styles/colors';

interface NoteProps {
  additionalNote: OfferConditionsType | undefined;
  handleNote: (value: LanguageMap<string> | undefined) => void;
  isReadOnly?: boolean;
  showToolbar?: boolean;
  showHeader?: boolean;
  height?: string;
  title?: string;
  disableLanguageSupport?: boolean;
}

const languageLabels: LanguageMap<string> = {
  en: 'English',
  fr: 'French',
  es: 'Spanish',
};

export const AdditionalNoteEditor = ({
  additionalNote,
  handleNote,
  isReadOnly,
  showToolbar,
  showHeader,
  height,
  title,
  disableLanguageSupport,
}: NoteProps): JSX.Element => {
  const { t } = useTranslation();
  const editorRef = React.useRef(null);
  const boxRef = React.useRef<HTMLDivElement>(null);

  const [selectedLanguage, setSelectedLanguage] = useState<string>(i18n.language);
  const [currentNote, setCurrentNote] = useState<LanguageMap<string> | undefined>(additionalNote as LanguageMap<string>);
  const triggeredByChangeRef = React.useRef(false);

  useEffect(() => {
    // istanbul ignore else
    if (additionalNote) {
      const normalizedNote: LanguageMap<string> = isObject(additionalNote)
        ? additionalNote
        : // istanbul ignore next
          { en: additionalNote };

      setCurrentNote((prev) => {
        if (JSON.stringify(prev) !== JSON.stringify(normalizedNote)) {
          handleNote(normalizedNote);
          return normalizedNote;
        }
        return prev;
      });
    } else {
      setCurrentNote(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalNote]);

  // istanbul ignore next
  useEffect(() => {
    if (triggeredByChangeRef.current && currentNote) {
      handleNote(currentNote);
      triggeredByChangeRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNote]);

  // istanbul ignore next
  const handleChange = React.useCallback(
    (newContent: string) => {
      if (editorRef.current) {
        let contentText = newContent;

        if (newContent === '<p><br></p>') {
          contentText = '';
        }

        // ROOT CAUSE FIX for Undo/Mode-switch:
        // Only trigger updates if content has actually changed.This prevents React from pushing the value back into Jodit unnecessarily, which is what clears the Undo history and interrupts mode transitions.
        const currentLangValue = currentNote?.[selectedLanguage as keyof LanguageMap<string>] ?? '';

        if (currentLangValue !== contentText) {
          const updatedNote: LanguageMap<string> = { ...currentNote, [selectedLanguage]: contentText };
          setCurrentNote(updatedNote);
          handleNote(updatedNote);
        }
      }
    },
    [currentNote, selectedLanguage, handleNote],
  );

  const memoizedConfig = useMemo(
    () => ({
      readonly: isReadOnly,
      height: height ?? '150px',
      toolbar: showToolbar,
      placeholder: '',
      toolbarAdaptive: false,
      removeButtons: [
        'image',
        'video',
        'file',
        'speechRecognize',
        'symbol',
        'print',
        'about',
        'fullsize',
        'selectall',
        'cut',
        'copy',
        'paste',
        'spellcheck',
        'eraser',
        'table',
        'superscript',
        'subscript',
        'classname',
        'font',
        'fontsize',
        'paragraph',
        'lineHeight',
        'fontFamily',
        'ai-commands',
        'ai-assistant',
        'symbols',
        'indent',
        'outdent',
        'brush',
        'classSpan',
        'copyformat',
        'left',
      ],
      controls: {
        ul: {
          list: Jodit.atom({
            default: 'Default',
          }),
        },
        ol: {
          list: Jodit.atom({
            default: 'Default',
          }),
        },
      },
      cleanHTML: {
        removeEmptyElements: false,
      },
      enter: 'p' as const,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isReadOnly, height, showToolbar, t],
  );

  const descriptionText =
    currentNote?.[selectedLanguage as keyof LanguageMap<string>] ??
    // istanbul ignore next
    '';

  return (
    <Box w="100%" mb={6} mt={4} position="relative">
      <FormControl>
        {showHeader && (
          <Flex justifyContent="space-between" alignItems="center" mb={1}>
            <Flex display="flex" justifyContent="flex-start" alignItems="center">
              {title ? (
                <Text fontSize="16px" mr={2} fontWeight={700} pb={2}>
                  {title}
                </Text>
              ) : (
                <>
                  <Text fontSize="15px" mr={2} fontWeight={700} pb={2}>
                    {t('createJobPosition:offerTerms.additionalOffer')}
                  </Text>
                  <Tooltip hasArrow placement="right" label={t('createJobPosition:offerTerms.tooltipText')}>
                    <Box pb={2}>
                      <MdOutlineInfo />
                    </Box>
                  </Tooltip>
                </>
              )}
            </Flex>
            {!disableLanguageSupport && (
              <Box width="100px">
                <Select
                  value={selectedLanguage}
                  data-testid="selectLanguage"
                  id="select-language"
                  onChange={(e) => {
                    setSelectedLanguage?.(e.target.value);
                  }}
                  fontSize="12px"
                  backgroundColor={colors.input.background}
                  _placeholder={{ color: colors.input.placeholderColor }}
                  style={{ height: '30px' }}
                >
                  <option value="" selected disabled>
                    {t('createJobPosition:offerTerms.selectLanguage')}
                  </option>
                  {Object.entries(languageLabels).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </Select>
              </Box>
            )}
          </Flex>
        )}
        <Box ref={boxRef}>
          <JoditEditor
            key={selectedLanguage}
            ref={editorRef}
            value={descriptionText}
            config={memoizedConfig}
            onChange={handleChange}
            data-testid="jodit-editor"
          />
        </Box>
      </FormControl>
    </Box>
  );
};
AdditionalNoteEditor.defaultProps = {
  isReadOnly: false,
  showToolbar: true,
  showHeader: true,
};
